import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageKey, StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  backgroundUrl = 'blurred-spring-background.svg';
  frameBackgroundUrl = 'spring-background.svg';
  startDate: Date;
  endDate: Date;
  dayCount: number;
  currentDate = new Date();
  // showEndDatePicker = false;
  showEndDatePicker: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showEndDateRequest = false;
  showWelcomeMessage = true;
  dateDifference = 1;
  endNowDateDifference: number;
  trainWidth = 16.5;
  today = new Date();
  dateRange = [];
  startDateRange = [];
  endDateRange = [];
  kofferStage: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  missionsToUpgrade: BehaviorSubject<number> = new BehaviorSubject<number>(5);
  firstLogin: boolean;

  introduction: boolean;
  showDatePicker: boolean;
  firstLoginDate: Date;

  startDateDifference: number;
  showRequestEndDateOnSecondDay = false;
  dontShowSecondDayEndDateRequest = true;

  constructor(private storageService: StorageService) {
    this.introduction = this.storageService.get(StorageKey.INTRODUCTION);
    this.showDatePicker = this.introduction;
    this.firstLoginDate = this.storageService.get(StorageKey.FIRST_LOGIN_DATE);

    this.showEndDateRequest = !this.storageService.has(StorageKey.END_DATE);
  }

  /**
   * Because ion-datetime isn't correctly typed we also need to accept strings.
   * @param start
   * @param end
   */
  setStartDateOrEndDate(start?: Date | string, end?: Date | string) {
    if (start) this.storageService.set(StorageKey.START_DATE, new Date(start));
    if (end) this.storageService.set(StorageKey.END_DATE, new Date(end));
  }

  //here we determine the difference between the current date and the start date
  determineDayDifferenceFromStartDate(): void {
    this.dateDifference = this.calculateDateDifference(this.storageService.get(StorageKey.START_DATE), new Date());
  }

  toggleSecondDayEndRequest() {
    this.startDateDifference = this.calculateDateDifference(this.storageService.get(StorageKey.START_DATE), new Date());

    if (this.startDateDifference === 2 && !this.storageService.get(StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY)) {
      this.showRequestEndDateOnSecondDay = true;
      this.storageService.set(StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY, true);
    } else {
      this.showRequestEndDateOnSecondDay = false;
      this.dontShowSecondDayEndDateRequest = false;
      this.storageService.set(StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY, false);
    }
  }

  //shows the train conductor's welcome or end date request message
  turnOffEndDateRequest(): void {
    this.showEndDateRequest = false;
    this.storageService.set(StorageKey.SHOW_END_DATE_FIRST_REQUEST, false);

    if (!this.storageService.get(StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY)) {
      this.storageService.set(StorageKey.SHOW_END_DATE_REQUEST, new Date());
    }
  }

  turnOnEndDateRequest(): void {
    this.showEndDateRequest = true;
  }

  turnOffShowWelcome(): void {
    this.showWelcomeMessage = false;
    this.storageService.set(StorageKey.WELCOME_MESSAGE_DATE, new Date());
    this.firstLogin = false;
  }

  turnOnShowWelcome(): void {
    this.showWelcomeMessage = true;
  }

  turnOffFirstLoginCloudText(): void {
    this.firstLogin = false;
    this.storageService.set(StorageKey.FIRST_LOGIN_DATE, new Date());
    this.showWelcomeMessage = false;
    this.showEndDateRequest = false;
  }

  /**
   * Calculates the difference between two dates, and returns the number of days rounded up.
   * For example, Comparing monday at 8 with tuesday at 7 will return 1, but comparing monday at 8 with tuesday at 9 will return 2.
   * @param start Representation of the Date. Will be passed to the Date
   * @param end
   */
  calculateDateDifference(start: number | string | Date, end: number | string | Date): number {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(startDate.getTime() - endDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  //sets the width of the day counter train based on the start date difference
  setTrainWidthBasedOnDateDifference(): void {
    if (this.endNowDateDifference) {
      switch (this.endNowDateDifference % 7) {
        case 0:
          this.trainWidth = 100;
          break;
        case 6:
          this.trainWidth = 16.5;
          break;
        case 5:
          this.trainWidth = 30.5;
          break;
        case 4:
          this.trainWidth = 45;
          break;
        case 3:
          this.trainWidth = 59;
          break;
        case 2:
          this.trainWidth = 72;
          break;
        case 1:
          this.trainWidth = 85.5;
          break;
        default:
          this.trainWidth = 18.5;
          break;
      }
      return;
    }

    switch (this.dateDifference % 7) {
      case 0:
        this.trainWidth = 100;
        break;
      case 1:
        this.trainWidth = 16.5;
        break;
      case 2:
        this.trainWidth = 30.5;
        break;
      case 3:
        this.trainWidth = 45;
        break;
      case 4:
        this.trainWidth = 59;
        break;
      case 5:
        this.trainWidth = 72;
        break;
      case 6:
        this.trainWidth = 85.5;
        break;
      default:
        this.trainWidth = 18.5;
        break;
    }
  }

  //gets the week number from any month
  getDateDifferenceInWeeks(date: Date): number {
    return Math.floor((Number(new Date()) - Number(new Date(date))) / 1000 / 60 / 60 / 24 / 7);
  }

  //sets the background based on the week number
  setBackgroundBasedOnDate(): void {
    if (!this.storageService.has(StorageKey.BACKGROUND_DATE)) {
      this.storageService.set(StorageKey.BACKGROUND_DATE, new Date());
    }

    const dateDifference = this.endNowDateDifference
      ? this.getDateDifferenceInWeeks(this.storageService.get(StorageKey.BACKGROUND_DATE))
      : this.getDateDifferenceInWeeks(this.storageService.get(StorageKey.BACKGROUND_DATE));

    switch (dateDifference % 4) {
      case 1:
        this.frameBackgroundUrl = 'large-summer-background.svg';
        this.backgroundUrl = 'blurred-summer-background.svg';
        break;
      case 2:
        this.frameBackgroundUrl = 'large-autumn-background.svg';
        this.backgroundUrl = 'blurred-fall-background.svg';
        break;
      case 3:
        this.frameBackgroundUrl = 'large-winter-background.svg';
        this.backgroundUrl = 'blurred-snow-background.svg';
        break;
      case 0:
      default:
        this.frameBackgroundUrl = 'large-spring-background.svg';
        this.backgroundUrl = 'blurred-spring-background.svg';
    }
  }

  //this is very ugly, it builds the array which shows which day we are from start date
  buildStartDateRange(): void {
    switch (this.dateDifference % 7) {
      case 0:
        this.dateRange = [
          this.dateDifference - 6,
          this.dateDifference - 5,
          this.dateDifference - 4,
          this.dateDifference - 3,
          this.dateDifference - 2,
          this.dateDifference - 1,
          this.dateDifference,
        ];
        break;
      case 1:
        this.dateRange = [];
        for (let i = this.dateDifference; this.dateRange.length < 7; i++) {
          this.dateRange.push(i);
        }
        break;
      case 2:
        this.dateRange = [];
        this.dateRange = [
          this.dateDifference - 1,
          this.dateDifference,
          this.dateDifference + 1,
          this.dateDifference + 2,
          this.dateDifference + 3,
          this.dateDifference + 4,
          this.dateDifference + 5,
        ];
        break;
      case 3:
        this.dateRange = [
          this.dateDifference - 2,
          this.dateDifference - 1,
          this.dateDifference,
          this.dateDifference + 1,
          this.dateDifference + 2,
          this.dateDifference + 3,
          this.dateDifference + 4,
        ];
        break;
      case 4:
        this.dateRange = [
          this.dateDifference - 3,
          this.dateDifference - 2,
          this.dateDifference - 1,
          this.dateDifference,
          this.dateDifference + 1,
          this.dateDifference + 2,
          this.dateDifference + 3,
        ];
        break;
      case 5:
        this.dateRange = [
          this.dateDifference - 4,
          this.dateDifference - 3,
          this.dateDifference - 2,
          this.dateDifference - 1,
          this.dateDifference,
          this.dateDifference + 1,
          this.dateDifference + 2,
        ];
        break;
      case 6:
        this.dateRange = [
          this.dateDifference - 5,
          this.dateDifference - 4,
          this.dateDifference - 3,
          this.dateDifference - 2,
          this.dateDifference - 1,
          this.dateDifference,
          this.dateDifference + 1,
        ];
        break;
    }
  }

  //this is very ugly, it builds the array that counts down to the end date
  buildEndDateRange(): void {
    this.endNowDateDifference = this.calculateDateDifference(this.storageService.get(StorageKey.END_DATE), new Date());

    switch (this.endNowDateDifference % 7) {
      case 0:
        this.dateRange = [
          this.endNowDateDifference + 6,
          this.endNowDateDifference + 5,
          this.endNowDateDifference + 4,
          this.endNowDateDifference + 3,
          this.endNowDateDifference + 2,
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
        ];
        break;
      case 6:
        this.dateRange = [];
        this.dateRange = [
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
          this.endNowDateDifference - 2,
          this.endNowDateDifference - 3,
          this.endNowDateDifference - 4,
          this.endNowDateDifference - 5,
          this.endNowDateDifference - 6,
        ];
        break;
      case 5:
        this.dateRange = [
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
          this.endNowDateDifference - 2,
          this.endNowDateDifference - 3,
          this.endNowDateDifference - 4,
          this.endNowDateDifference - 5,
        ];
        break;
      case 4:
        this.dateRange = [
          this.endNowDateDifference + 2,
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
          this.endNowDateDifference - 2,
          this.endNowDateDifference - 3,
          this.endNowDateDifference - 4,
        ];
        break;
      case 3:
        this.dateRange = [
          this.endNowDateDifference + 3,
          this.endNowDateDifference + 2,
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
          this.endNowDateDifference - 2,
          this.endNowDateDifference - 3,
        ];
        break;
      case 2:
        this.dateRange = [
          this.endNowDateDifference + 4,
          this.endNowDateDifference + 3,
          this.endNowDateDifference + 2,
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
          this.endNowDateDifference - 2,
        ];
        break;
      case 1:
        this.dateRange = [
          this.endNowDateDifference + 5,
          this.endNowDateDifference + 4,
          this.endNowDateDifference + 3,
          this.endNowDateDifference + 2,
          this.endNowDateDifference + 1,
          this.endNowDateDifference,
          this.endNowDateDifference - 1,
        ];
        break;
    }

    this.setTrainWidthBasedOnDateDifference();
  }

  //builds the day counter date range
  buildDateRange(): void {
    const startDate = this.storageService.get(StorageKey.START_DATE);
    const endDate = this.storageService.get(StorageKey.END_DATE);

    //if only start date
    if (startDate && !endDate) {
      this.dateRange = [];
      this.startDateRange = [];
      this.endDateRange = [];

      const startNowDateDifference = this.calculateDateDifference(startDate, new Date());
      for (let i = 1; i <= startNowDateDifference; i++) {
        this.startDateRange.push(i);
      }

      this.dateRange.push(this.startDateRange.slice(0, 7));
      this.buildStartDateRange();
    }

    if (startDate && endDate) {
      this.buildEndDateRange();
    }
  }

  skipDay(): void {
    let currentStartDate = this.storageService.get(StorageKey.START_DATE);
    if (!currentStartDate) return;

    let currentEndDate = this.storageService.get(StorageKey.END_DATE);
    let showEndDateRequest = this.storageService.get(StorageKey.SHOW_END_DATE_REQUEST);
    let welcomeMessageDate = this.storageService.get(StorageKey.WELCOME_MESSAGE_DATE);
    let backgroundDate = this.storageService.get(StorageKey.BACKGROUND_DATE);
    let firstLoginDate = this.storageService.get(StorageKey.FIRST_LOGIN_DATE);
    let updatedSliceAmountDate = this.storageService.get(StorageKey.UPDATED_SLICE_AMOUNT);

    currentStartDate = new Date(currentStartDate.getTime() - 24 * 60 * 60 * 1000);
    showEndDateRequest = new Date(showEndDateRequest.getTime() - 24 * 60 * 60 * 1000);
    welcomeMessageDate = new Date(welcomeMessageDate.getTime() - 24 * 60 * 60 * 1000);
    backgroundDate = new Date(backgroundDate.getTime() - 24 * 60 * 60 * 1000);
    currentEndDate = new Date(currentEndDate.getTime() - 24 * 60 * 60 * 1000);
    firstLoginDate = new Date(firstLoginDate.getTime() - 24 * 60 * 60 * 1000);
    updatedSliceAmountDate = new Date(updatedSliceAmountDate.getTime() - 24 * 60 * 60 * 1000);

    this.storageService.set(StorageKey.START_DATE, currentStartDate);
    this.storageService.set(StorageKey.SHOW_END_DATE_REQUEST, showEndDateRequest);
    this.storageService.set(StorageKey.WELCOME_MESSAGE_DATE, welcomeMessageDate);
    this.storageService.set(StorageKey.BACKGROUND_DATE, backgroundDate);
    this.storageService.set(StorageKey.MISSIONS_FINISHED_TODAY, 0);
    this.storageService.set(StorageKey.UPDATED_SLICE_AMOUNT, updatedSliceAmountDate);
    this.storageService.set(StorageKey.FIRST_LOGIN_DATE, firstLoginDate);

    if (this.storageService.has(StorageKey.END_DATE)) {
      this.storageService.set(StorageKey.END_DATE, currentEndDate);
    }

    this.startDate = currentStartDate;
    this.setBackgroundBasedOnDate();
    this.setTrainWidthBasedOnDateDifference();
    this.buildDateRange();

    window.location.reload();
  }
}
